import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';

/*
 * A deploy replaces the hashed chunks this page was built to load. A tab left
 * open across one is holding an index that names files the CDN no longer has,
 * so the next lazily-loaded page 404s and the router shows a crash for what is
 * really staleness.
 *
 * The fix is to leave, not to patch: navigating is the one moment where a full
 * page load costs nothing anybody notices, so the next navigation after a
 * deploy is turned into a real one.
 */

const CHECK_COOLDOWN_MS = 5 * 60 * 1000;

/*
 * Module state rather than a ref: the answer is about this document, not about
 * a component, and the hook is mounted once at the root either way. Keeping it
 * here means a remount cannot forget that the tab is already stale.
 */
let isStale = false;
let lastCheckAt = 0;

async function checkVersion(): Promise<void> {
  const now = Date.now();
  if (now - lastCheckAt < CHECK_COOLDOWN_MS) return;
  lastCheckAt = now;

  try {
    const response = await fetch('/version.json', { cache: 'no-cache' });
    if (!response.ok) return;

    const { version } = (await response.json()) as { version?: string };
    const built = import.meta.env.VITE_APP_VERSION;

    if (version && built && version !== built) {
      isStale = true;
    }
  } catch {
    /* Offline, or the file is not there. Nothing said is not "changed". */
  }
}

/**
 * Watch for a deploy under an open tab, and spend the next navigation on
 * picking it up.
 *
 * Asked after each navigation and whenever the tab comes back to the front,
 * at most once every five minutes. A stale tab then turns its next navigation
 * into a full page load, and a chunk that fails to preload — the same
 * staleness, noticed the hard way — reloads on the spot.
 */
export function useVersionCheck(): void {
  const router = useRouter();

  useEffect(() => {
    const unsubscribeResolved = router.subscribe('onResolved', () => {
      void checkVersion();
    });

    const unsubscribeBeforeLoad = router.subscribe(
      'onBeforeNavigate',
      ({ toLocation }) => {
        if (isStale) {
          window.location.assign(toLocation.href);
        }
      },
    );

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    }

    /*
     * A chunk that will not load is the same staleness, found the hard way and
     * with no navigation left to spend on it — the page has already failed.
     */
    function onPreloadError() {
      window.location.reload();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('vite:preloadError', onPreloadError);

    return () => {
      unsubscribeResolved();
      unsubscribeBeforeLoad();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, [router]);
}
